import { useState, useEffect, useContext } from "react";
import { Row, Col, Tabs, Dropdown, Menu } from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  RedoOutlined,
  CheckOutlined,
  MoreOutlined,
} from "@ant-design/icons";

import { TourContext } from "../../context/TourContext";

import DataTable from "../DataTable";
import CopyableCell from "../CopyableCell";
import {
  clampPage,
  getStoredActiveTab,
  getStoredTabPage,
  resolveActiveTab,
  resolveTabPage,
  setStoredActiveTab,
  setStoredTabPage,
  visibleStatusTabs,
} from "../../utils/tablePaginationStorage";

const { TabPane } = Tabs;

interface IUserProps {
  users: any;
  showEditModal: Function;
  handleResend: Function;
  handleDelete: Function;
  filtered: any;
  isSuperAdmin: boolean;
  storagePrefix?: string;
}

const UserTable: React.FC<IUserProps> = ({
  users,
  showEditModal,
  handleResend,
  handleDelete,
  filtered,
  isSuperAdmin,
  storagePrefix = "users",
}) => {
  const filterSuperAdmins = (list: any[]) =>
    isSuperAdmin ? list : list.filter((u: any) => u?.Role?.Name !== "Super Admin");
  const { refEditUser, refRemoveUser, markPageAsVisited, pages } =
    useContext(TourContext);

  const initialTab = getStoredActiveTab(storagePrefix);

  const [active, setActive] = useState([]);
  const [inactive, setInactive] = useState([]);
  const [pending, setPending] = useState([]);

  const [filteredActive, setFilteredActive] = useState([]);
  const [filteredInActive, setFilteredInActive] = useState([]);
  const [filteredPending, setFilteredPending] = useState([]);

  const [state, setState] = useState({
    loading: true,
    activeKey: initialTab,
    pagination: {
      pageSize: 15,
      position: ["bottomRight"],
      total: 0,
      current: getStoredTabPage(storagePrefix, initialTab),
      hideOnSinglePage: true,
    },
    searchItem: "",
  });

  const applyTabTotal = (tabKey: string, total: number) => {
    setState((prev) => {
      if (prev.activeKey !== tabKey) {
        return { ...prev, loading: false };
      }
      const pageSize = prev.pagination.pageSize || 15;
      const current = resolveTabPage(
        storagePrefix,
        tabKey,
        prev.pagination.current || 1,
        total,
        pageSize
      );
      return {
        ...prev,
        loading: false,
        pagination: {
          ...prev.pagination,
          total,
          current,
        },
      };
    });
  };

  const fetchActive = async () => {
    try {
      const activeUsers = filterSuperAdmins(
        users.filter((item: any) => item.Status.Status === "Active")
      );

      let activeFiltered: any[] = [];
      if (filtered.length > 0) {
        activeFiltered = filterSuperAdmins(
          filtered.filter((item: any) => item.Status.Status === "Active")
        );
        setFilteredActive(activeFiltered);
      } else {
        setFilteredActive([]);
      }

      const total =
        filtered.length > 0 ? activeFiltered.length : activeUsers.length;

      applyTabTotal("1", total);
      setActive(activeUsers);
    } catch (err) {
      console.log(err);
    }
  };

  const fetchInActive = async () => {
    try {
      const inactiveUsers = filterSuperAdmins(
        users.filter((item: any) => item.Status.Status === "Inactive")
      );

      let inActiveFiltered: any[] = [];
      if (filtered.length > 0) {
        inActiveFiltered = filterSuperAdmins(
          filtered.filter((item: any) => item.Status.Status === "Inactive")
        );
        setFilteredInActive(inActiveFiltered);
      } else {
        setFilteredInActive([]);
      }

      const total =
        filtered.length > 0 ? inActiveFiltered.length : inactiveUsers.length;

      applyTabTotal("2", total);
      setInactive(inactiveUsers);
    } catch (err) {
      console.log(err);
    }
  };

  const fetchPending = async () => {
    try {
      const pendingUsers = filterSuperAdmins(
        users.filter((item: any) => item.Status.Status === "Pending")
      );

      let pendingFiltered: any[] = [];
      if (filtered.length > 0) {
        pendingFiltered = filterSuperAdmins(
          filtered.filter((item: any) => item.Status.Status === "Pending")
        );
        setFilteredPending(pendingFiltered);
      } else {
        setFilteredPending([]);
      }

      const total =
        filtered.length > 0 ? pendingFiltered.length : pendingUsers.length;

      applyTabTotal("3", total);
      setPending(pendingUsers);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    if (filtered.length === 0) {
      setFilteredActive([]);
      setFilteredInActive([]);
      setFilteredPending([]);
    }
    fetchActive();
    fetchInActive();
    fetchPending();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users, filtered]);

  useEffect(() => {
    if (pages.ManageMember) {
      markPageAsVisited("UserTable");
    }
  }, []);

  const handleTableChange = (pagination: any) => {
    setStoredTabPage(storagePrefix, state.activeKey, pagination.current);
    setState((prev) => ({
      ...prev,
      pagination: {
        ...prev.pagination,
        current: pagination.current,
        pageSize: pagination.pageSize ?? prev.pagination.pageSize,
      },
    }));
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "Name",
      sorter: (a: any, b: any) => a.Name.localeCompare(b.Name),
    },
    {
      title: "Surname",
      dataIndex: "Surname",
      sorter: (a: any, b: any) => a.Surname.localeCompare(b.Surname),
    },
    {
      title: "Email",
      dataIndex: "Email",
      render: (v: any) => <CopyableCell value={v} />,
    },
    {
      title: "Cellphone Number",
      dataIndex: "Cellphone",
    },
    {
      title: "Role",
      dataIndex: ["Role", "Name"],
    },
    {
      title: "Group/s",
      dataIndex: ["Group", "GroupName"],
      render: (_: any, record: any) => (
        <span>
          {record.Group.map((group: any) => group.GroupName).join(", ")}
        </span>
      ),
    },
    {
      dataIndex: "Action",
      render: (_: any, record: any) => (
        <Dropdown
          overlay={
            <Menu>
              <Menu.Item
                key="edit"
                icon={<EditOutlined ref={refEditUser} style={{ color: "blue" }} />}
                onClick={() => showEditModal(record)}
              >
                Edit User
              </Menu.Item>
              {state.activeKey === "1" && (
                <Menu.Item
                  key="remove"
                  icon={<DeleteOutlined ref={refRemoveUser} style={{ color: "red" }} />}
                  onClick={() => handleDelete(record.id, state.activeKey)}
                >
                  Remove User
                </Menu.Item>
              )}
              {state.activeKey === "2" && (
                <Menu.Item
                  key="activate"
                  icon={<CheckOutlined style={{ color: "orange" }} />}
                  onClick={() => handleDelete(record.id, state.activeKey)}
                >
                  Activate User
                </Menu.Item>
              )}
            </Menu>
          }
          trigger={["click"]}
        >
          <MoreOutlined
            rotate={90}
            style={{ cursor: "pointer", fontSize: "16px", color: "#000" }}
          />
        </Dropdown>
      ),
    },
  ];

  const pendingColumn = [
    {
      title: "Name",
      dataIndex: "Name",
    },
    {
      title: "Surname",
      dataIndex: "Surname",
    },
    {
      title: "Email",
      dataIndex: "Email",
      render: (v: any) => <CopyableCell value={v} />,
    },
    {
      title: "Cellphone Number",
      dataIndex: "Cellphone",
    },
    {
      title: "Role",
      dataIndex: ["Role", "Name"],
    },
    {
      title: "Group",
      dataIndex: ["Group", "GroupName"],
    },
    {
      dataIndex: "Action",
      render: (_: any, record: any) => (
        <Dropdown
          overlay={
            <Menu>
              <Menu.Item
                key="resend"
                icon={<RedoOutlined style={{ color: "green" }} />}
                onClick={() => handleResend(record.id)}
              >
                Resend Verification
              </Menu.Item>
              <Menu.Item
                key="remove"
                icon={<DeleteOutlined style={{ color: "red" }} />}
                onClick={() => handleDelete(record.id, state.activeKey)}
              >
                Remove User
              </Menu.Item>
            </Menu>
          }
          trigger={["click"]}
        >
          <MoreOutlined
            rotate={90}
            style={{ cursor: "pointer", fontSize: "16px", color: "#000" }}
          />
        </Dropdown>
      ),
    },
  ];

  const tabPanes = [
    {
      key: "1",
      title: "Active",
      content: (
        <DataTable
          persistKey="users"
          dataSource={filteredActive.length > 0 ? filteredActive : active}
          columns={columns}
          loading={state.loading}
          pagination={state.pagination}
          onChange={handleTableChange}
        />
      ),
      count: filteredActive.length > 0 ? filteredActive.length : active.length,
    },
    {
      key: "2",
      title: "Inactive",
      content: (
        <DataTable
          persistKey="users"
          dataSource={filteredInActive.length > 0 ? filteredInActive : inactive}
          columns={columns}
          loading={state.loading}
          pagination={state.pagination}
          onChange={handleTableChange}
        />
      ),
      count:
        filteredInActive.length > 0 ? filteredInActive.length : inactive.length,
    },
    {
      key: "3",
      title: "Pending",
      content: (
        <DataTable
          persistKey="users"
          dataSource={filteredPending.length > 0 ? filteredPending : pending}
          columns={pendingColumn}
          loading={state.loading}
          pagination={state.pagination}
          onChange={handleTableChange}
        />
      ),
      count:
        filteredPending.length > 0 ? filteredPending.length : pending.length,
    },
  ];

  const visiblePanes = visibleStatusTabs(tabPanes);
  const activeKey = resolveActiveTab(state.activeKey, visiblePanes);

  const changeTab = (key: string) => {
    setStoredActiveTab(storagePrefix, key);

    let total = 0;
    if (key === "1") {
      total = filteredActive.length > 0 ? filteredActive.length : active.length;
    } else if (key === "2") {
      total = filteredInActive.length > 0 ? filteredInActive.length : inactive.length;
    } else {
      total = filteredPending.length > 0 ? filteredPending.length : pending.length;
    }

    const pageSize = state.pagination.pageSize || 15;
    const storedPage = getStoredTabPage(storagePrefix, key);
    const current = clampPage(storedPage, total, pageSize);
    setStoredTabPage(storagePrefix, key, current);

    setState((prev) => ({
      ...prev,
      activeKey: key,
      loading: false,
      pagination: {
        ...prev.pagination,
        current,
        total,
      },
    }));
  };

  return (
    <>
      <Row>
        <Col span={24}>
          <Tabs
            type="card"
            activeKey={activeKey}
            onChange={changeTab}
          >
            {visiblePanes.map((pane) => (
              <TabPane
                key={pane.key}
                tab={
                  <>
                    <span>{pane.title}</span>
                    <span
                      style={{
                        color: "#B0B0B0",
                        fontWeight: "300",
                        marginLeft: "3px",
                        fontSize: "12px",
                      }}
                    >
                      ({pane.count})
                    </span>
                  </>
                }
              >
                {pane.content}
              </TabPane>
            ))}
          </Tabs>
        </Col>
      </Row>
    </>
  );
};

export default UserTable;
